import { z } from 'zod';
import { Money } from '@urbanrats/contracts';

/**
 * UrbanRats embed protocol v1 (docs §10 "iframe SDK"). Every message carries protocol + version; money
 * is { amountAtomic, currency, scale } — formatting is left to the partner adapter. Messages from the
 * parent can only ask the game to re-read its gateways or change local UI settings: they can never
 * create, change or settle a bet, a balance or a result.
 */
export const EMBED_PROTOCOL = 'urbanrats.embed';
export const EMBED_VERSION = 1;

const envelope = { protocol: z.literal(EMBED_PROTOCOL), version: z.literal(EMBED_VERSION) };
export const RequestId = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);

export const EmbedCommand = z.discriminatedUnion('command', [
  z.object({ ...envelope, kind: z.literal('command'), requestId: RequestId, command: z.literal('refresh-balance') }),
  z.object({ ...envelope, kind: z.literal('command'), requestId: RequestId, command: z.literal('mute'), args: z.object({ muted: z.boolean() }) }),
  z.object({ ...envelope, kind: z.literal('command'), requestId: RequestId, command: z.literal('volume'), args: z.object({ volume: z.number().min(0).max(1) }) }),
  z.object({ ...envelope, kind: z.literal('command'), requestId: RequestId, command: z.literal('open-history') }),
  z.object({ ...envelope, kind: z.literal('command'), requestId: RequestId, command: z.literal('close-panels') }),
  z.object({ ...envelope, kind: z.literal('command'), requestId: RequestId, command: z.literal('get-state') }),
]);
export type EmbedCommand = z.infer<typeof EmbedCommand>;
export type CommandName = EmbedCommand['command'];

export const GameState = z.object({
  gameId: z.string().nullable(),
  gameRoundId: z.string().nullable(),
  roundState: z.string().nullable(),
  balance: Money.nullable(),
  muted: z.boolean(),
  volume: z.number().min(0).max(1),
  openPanel: z.string().nullable(),
  sessionExpired: z.boolean(),
});
export type GameState = z.infer<typeof GameState>;

export const EmbedErrorCode = z.enum(['INVALID_MESSAGE', 'UNSUPPORTED_VERSION', 'RATE_LIMITED', 'COMMAND_FAILED', 'TIMEOUT']);

export const EmbedResponse = z.object({
  ...envelope,
  kind: z.literal('response'),
  requestId: RequestId,
  ok: z.boolean(),
  result: GameState.nullable(),
  error: z.object({ code: EmbedErrorCode, message: z.string().max(256) }).nullable(),
});
export type EmbedResponse = z.infer<typeof EmbedResponse>;

const BetRef = z.object({ betId: z.string(), gameRoundId: z.string(), selection: z.string(), stake: Money });

export const EmbedEvent = z.discriminatedUnion('event', [
  z.object({ ...envelope, kind: z.literal('event'), seq: z.number().int().positive(), event: z.literal('ready'), data: z.object({ gameId: z.string().nullable(), walletCurrency: z.string(), embedVersion: z.literal(EMBED_VERSION) }) }),
  z.object({ ...envelope, kind: z.literal('event'), seq: z.number().int().positive(), event: z.literal('error'), data: z.object({ code: z.string().max(64), message: z.string().max(256) }) }),
  z.object({ ...envelope, kind: z.literal('event'), seq: z.number().int().positive(), event: z.literal('session-expired'), data: z.object({}) }),
  z.object({ ...envelope, kind: z.literal('event'), seq: z.number().int().positive(), event: z.literal('round.phase'), data: z.object({ gameRoundId: z.string(), state: z.string() }) }),
  z.object({ ...envelope, kind: z.literal('event'), seq: z.number().int().positive(), event: z.literal('bet.accepted'), data: BetRef }),
  z.object({ ...envelope, kind: z.literal('event'), seq: z.number().int().positive(), event: z.literal('bet.rejected'), data: BetRef.extend({ reason: z.string().nullable() }) }),
  z.object({
    ...envelope,
    kind: z.literal('event'),
    seq: z.number().int().positive(),
    event: z.literal('bet.result'),
    data: BetRef.extend({ state: z.enum(['SETTLED', 'VOIDED']), credit: Money.nullable() }),
  }),
  z.object({ ...envelope, kind: z.literal('event'), seq: z.number().int().positive(), event: z.literal('balance.changed'), data: z.object({ balance: Money, balanceVersion: z.number().int().nonnegative() }) }),
]);
export type EmbedEvent = z.infer<typeof EmbedEvent>;
export type EventName = EmbedEvent['event'];
type EventData<E extends EventName> = Extract<EmbedEvent, { event: E }>['data'];
export type { EventData };
